<template>

    <headlessDialog open class="relative z-40">

        <div class="fixed inset-0 bg-black/50" aria-hidden="true"/>

        <div class="fixed inset-0 flex w-screen items-center justify-center">

            <DialogPanel
                    class="relative bg-white dark:bg-neutral-900 md:rounded max-w-md w-full h-full md:h-auto md:max-h-[80vh] overflow-hidden md:overflow-auto flex flex-col"
            >

                <div class="flex items-center justify-between p-4"
                     :class="[
                         $isStandalone ? '!pb-6': null,
                     ]"
                >

                    <Title class="mb-0">Exercise</Title>

                    <div class="flex items-center justify-between gap-x-6">
                        <div class="cursor-pointer" @click="handleTimerToggle"
                             v-if="user.settings.exerciseTimer && movement && !movement.trackDuration">
                            <Icon icon="stopwatch" class="text-2xl"/>
                        </div>
                        <div class="cursor-pointer" @click="handleCancel">
                            <Icon icon="times" class="text-2xl"/>
                        </div>
                    </div>

                </div>

                <div class="relative flex-grow overflow-scroll scroll-bar-hidden border-y border-y-neutral-300 dark:border-y-neutral-800 p-4 min-h-[46vh]">

                    <Field>
                        <Label>Movement</Label>
                        <SelectInput :options="movementsAsOptions" v-model="modelValue.movementId"></SelectInput>
                    </Field>

                    <template v-if="movement">

                        <Field v-if="user.settings.exerciseTimer && (movement.trackDuration || timer)">
                            <Label>Timer</Label>
                            <Timer v-model="modelValue.duration"
                                   :metronome="true"
                            />
                        </Field>

                        <template v-if="movement.trackResistance">

                            <template v-if="movement.resistanceType === 'band'">

                                <Field>

                                    <Label>Bands
                                        {{ movement.resistanceConfig && movement.resistanceConfig.bandDoubled ? `(Doubled)` : '' }}
                                    </Label>

                                    <BandsInput ref="bandsInput" v-model="modelValue.bandIds"
                                                :lastExercise="lastExercise"
                                                @update:modelValue="handleBandIdsUpdate"/>

                                    <Help class="mt-3 text-center" v-if="lastExercise && lastExercise.bands.length && !this.modelValue.bandIds.length">
                                        Previous: {{ lastExercise.bands.map(band => band.brandAndName).join(', ') }}
                                    </Help>
                                </Field>

                                <Field class="flex items-center justify-between"
                                       v-if="user.settings.exerciseAdvancedToggle">
                                    <p>Advanced</p>
                                    <Switch v-model="modelValue.advanced"/>
                                </Field>

                            </template>


                            <Field v-else>
                                <Label>Resistance ({{ movement.resistanceType }} in {{ weightDisplayUnit }})</Label>
                                <NumberInputUnitAware v-model="modelValue.weight"
                                                      baseUnit="kg"
                                                      :displayUnit="weightDisplayUnit"
                                                      class="text-center text-4xl py-6"
                                                      placeholder="0"
                                />
                                <Help class="py-1 text-center" v-if="lastExercise && lastExercise.weight">
                                    Previous:
                                    {{
                                        $numeral($convert(lastExercise.weight, 'kg').to(weightDisplayUnit)).format(weightDisplayFormat)
                                    }}
                                    {{ weightDisplayUnit }}
                                </Help>

                            </Field>

                        </template>

                        <template v-if="movement.trackRepetitions">

                            <div class="flex gap-x-4">

                                <Field :class="[
                                  movement.trackRepetitionsPartial ? 'w-1/2' : 'w-full',
                                ]">
                                    <Label class="text-center">Full Repetitions</Label>
                                    <NumberInput placeholder="0"
                                                 class="text-center text-4xl py-6"
                                                 v-model="modelValue.repetitions"/>
                                    <div class="flex">
                                        <Help class="py-1 text-center text-lg flex-1 font-bold dark:text-white text-black"
                                              data-cy="prev-reps"
                                              v-if="user.settings.metaPrevReps && lastExercise && lastExercise.repetitions !== undefined && lastExercise.repetitions !== null">
                                            Prev: {{ lastExercise.repetitions }}
                                        </Help>

                                        <template v-if="showMaxReps">
                                            <Help class="py-1 text-center text-lg flex-1 font-bold dark:text-white text-black"
                                                  data-cy="max-reps"
                                                  v-if="maxExercise && maxExercise.repetitions">
                                                Max: {{ maxExercise.repetitions }}
                                            </Help>
                                        </template>
                                    </div>
                                </Field>

                                <Field class="w-1/2" v-if="movement.trackRepetitionsPartial">
                                    <Label class="text-center">Partial Repetitions</Label>
                                    <NumberInput placeholder="0"
                                                 class="text-center text-4xl py-6"
                                                 v-model="modelValue.partialRepetitions"/>
                                    <Help class="py-1 text-center text-lg font-bold dark:text-white text-black"
                                          v-if="user.settings.metaPrevReps && lastExercise && lastExercise.partialRepetitions !== undefined && lastExercise.partialRepetitions !== null">
                                        Prev: {{ lastExercise.partialRepetitions }}
                                    </Help>
                                </Field>

                            </div>

                            <div v-if="user.settings.metaMaxRepsSource === 'any' && maxExercise?.bands" class="-mt-3 mb-3">
                                <Help class="text-center font-bold dark:text-white text-black">
                                    <span class="text-neutral-500">Your Max Reps were performed with</span><br />
                                    {{ maxExercise.bands.map(band => band.brandAndName).join(', ') }}
                                </Help>
                                <div class="text-center text-xs bg-brand-primary-100 text-neutral-950 rounded p-2 mt-6">
                                    You might want to update the Max Reps Source setting to use <span class="font-bold">Selected Band(s)</span>, which you can change in your
                                    <router-link to="/my/settings" class="text-brand-primary-500">settings</router-link>.
                                </div>
                            </div>


                        </template>

                        <Field v-if="user.settings.exerciseExhaustionLevel">
                            <div class="flex items-center justify-between">
                                <Label>Exhaustion Level</Label>
                                <button type="button"
                                        class="text-sm text-brand-primary-500"
                                        v-if="modelValue.exhaustionLevel"
                                        @click="handleExhaustionLevelClear">
                                    Clear
                                </button>
                            </div>

                            <div class="grid grid-cols-5 gap-2">
                                <button v-for="level in exhaustionLevels"
                                        :key="level"
                                        type="button"
                                        :data-cy="`exhaustion-level-${level}`"
                                        class="rounded border py-3 text-center font-semibold"
                                        :class="[
                                            modelValue.exhaustionLevel === level
                                              ? 'border-brand-primary-500 bg-brand-primary-100 text-neutral-950'
                                              : 'border-neutral-300 dark:border-neutral-700',
                                        ]"
                                        @click="handleExhaustionLevelSet(level)">
                                    {{ level }}
                                </button>
                            </div>

                            <Help class="py-1 text-center text-lg font-bold dark:text-white text-black"
                                  data-cy="prev-exhaustion-level"
                                  v-if="lastExercise && lastExercise.exhaustionLevel !== undefined && lastExercise.exhaustionLevel !== null">
                                Prev: {{ lastExercise.exhaustionLevel }}
                            </Help>
                        </Field>

                        <Field v-if="lastExercise && lastExercise.notes">
                            <div class="flex items-center justify-between">
                                <Label>Previous Notes</Label>
                                <div @click="handleLastExerciseNotesCopy">
                                    <Icon icon="copy"/>
                                </div>
                            </div>
                            <p class="mt-3 bg-neutral-200 dark:bg-neutral-950 px-3 py-2 rounded whitespace-pre-line">
                                {{ lastExercise.notes }}
                            </p>
                        </Field>

                        <Field v-if="user.settings.exerciseNotes">
                            <Label>Notes</Label>
                            <TextareaInput v-model="modelValue.notes"/>
                        </Field>

                    </template>

                </div>

                <div class="flex items-center justify-between p-4">

                    <div>
                        <Button danger outlined @click="handleDelete" v-if="modelValue.id">
                            <Icon icon="trash-alt"/>
                        </Button>
                    </div>

                    <div>
                        <Button block primary data-cy="save-exercise" @click="handleSave">
                            Save
                        </Button>
                    </div>

                </div>

            </DialogPanel>

        </div>

    </headlessDialog>

</template>

<script>
import {mapActions} from 'vuex';

import {
  Dialog as headlessDialog,
  DialogPanel,
} from '@headlessui/vue';

import BandsInput from '@/components/BandsInput.vue';
import Metronome from '@/components/Metronome.vue';
import Timer from '@/components/Timer.vue';

export default {
  name: 'Form',
  components: {
    BandsInput,
    DialogPanel,
    headlessDialog,
    Metronome,
    Timer,
  },
  computed: {
    lastExercise() {
      return this.lastExerciseId ? this.$store.getters['exercise/findBy'](this.lastExerciseId, 'id') : null;
    },
    maxExercise() {
      return this.maxExerciseId ? this.$store.getters['exercise/findBy'](this.maxExerciseId, 'id') : null;
    },
    showMaxReps() {
      if (!this.user.settings.metaMaxReps) {
        return false;
      }

      if (this.user.settings.metaMaxRepsSource === 'any') {
        return true;
      }

      if (this.user.settings.metaMaxRepsSource !== 'selected') {
        return false;
      }

      return this.modelValue.bandIds.length || this.movement?.resistanceType !== 'band';
    },
    exhaustionLevels() {
      return [1, 2, 3, 4, 5];
    },
    movement() {
      return this.modelValue.movementId ? this.movements.find(movement => movement.id === this.modelValue.movementId) : null;
    },
    movements() {
      return this.$store.getters['movement/all'] || [];
    },
    movementsAsOptions() {
      return [
        {
          label: '- Select Movement -',
          value: null,
        },
        ...this.movements.sort((a, b) => a.name > b.name ? 1 : -1).map((movement) => {
          return {
            label: movement.name,
            value: movement.id,
          };
        }),
      ];
    },
    user() {
      return this.$store.getters['auth/user'];
    },
    weightDisplayFormat() {
      return this.user.systemOfMeasure === 'metric' ? '0,0.0' : '0,0';
    },
    weightDisplayUnit() {
      return this.user.systemOfMeasure === 'metric' ? 'kg' : 'lb';
    },
  },
  created() {
    if ((this.modelValue.duration > 0 || this.user.settings.autoOpenTimer) && this.movement?.trackDuration) {
      this.timer = true;
    }
  },
  data() {
    return {
      lastExerciseId: null,
      loading: false,
      maxExerciseId: null,
      maxExerciseRequestNumber: 0,
      timer: false,
    }
  },
  methods: {
    ...mapActions({
      exerciseLast: 'exercise/last',
      exerciseMax: 'exercise/max',
    }),
    handleCancel() {
      this.$emit('cancel');
    },
    handleDelete() {
      this.$emit('delete');
    },
    handleExhaustionLevelClear() {
      this.modelValue.exhaustionLevel = null;
    },
    handleExhaustionLevelSet(value) {
      this.modelValue.exhaustionLevel = value;
    },
    handleLastExerciseNotesCopy() {
      this.modelValue.notes = this.lastExercise.notes;
    },
    handleBandIdsUpdate(bandIds) {
      this.modelValue.bandIds = bandIds;
    },
    handleSave() {
      this.$emit('save');
    },
    handleTimerToggle() {
      this.timer = !this.timer;
    },
    loadLastExercise() {

      if (this.loading) {
        return;
      }

      this.loading = true;

      return this.exerciseLast({
        params: {
          exerciseId: this.modelValue.id || null,
          movementId: this.modelValue.movementId,
          rank: this.modelValue.rank,
          workoutId: this.modelValue.workoutId,
        },
      }).then((ids) => {
        this.lastExerciseId = ids.length ? ids[0] : null;
      }).finally(() => {
        this.loading = false;
      });
    },
    loadMaxExercise(bandIds = null) {

      const requestNumber = ++this.maxExerciseRequestNumber;

      return this.exerciseMax({
        params: {
          exerciseId: this.modelValue.id || null,
          movementId: this.modelValue.movementId,
          bandIds: bandIds || this.modelValue.bandIds || [],
        },
      }).then((ids) => {
        if (requestNumber === this.maxExerciseRequestNumber) {
          this.maxExerciseId = ids.length ? ids[0] : null;
        }
      });
    },
  },
  props: {
    modelValue: {
      required: true,
    }
  },
  watch: {
    ['modelValue.bandIds']: {
      deep: true,
      async handler() {
        if (this.modelValue.movementId) {
          this.loadMaxExercise();
        }
      },
    },
    ['modelValue.movementId']: {
      immediate: true,
      async handler() {
        if (this.modelValue.movementId) {

          await this.loadLastExercise().then(async () => {
            if (this.user.settings.autoLastBands && !this.modelValue.bandIds.length) {
              await this.$nextTick(() => {
                this.$refs.bandsInput?.handleBandsUsePrevious();
              });
            }
          });

          await this.$nextTick(() => {
            if (this.user.settings.metaMaxReps) {
              this.loadMaxExercise();
            }
          });
        }
      },
    },
  },
};
</script>

<style scoped lang="scss">

</style>
